import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import {
  connectorsByIdentifier,
  connectorTypeOf,
} from "../../shared/connectors";
import { findConnectorTypes } from "../../shared/connectors/findConnectorTypes";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useCreateConnector } from "../hooks/useCreateConnector";
import { usePersonalWorkEnabled } from "../hooks/usePersonalWorkEnabled";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useSpaces } from "../hooks/useSpaces";
import { clientSendContext } from "../lib/clientSendContext";
import { connectorDirectory } from "../lib/connectorDirectory";
import { handOffPendingPersonalChat } from "../lib/pendingPersonalChats";
import { spaceIcon } from "../lib/spaceIcon";
import { navigate, routes, useMakeUrl } from "../lib/urls";
import {
  NoResults,
  NotAddedPill,
  SearchStatus,
  useCommandBarSearch,
} from "./CommandBarSearch";
import { ConnectorLogo } from "./ConnectorLogo";
import { EmptyState } from "./EmptyState";
import { Icon } from "./Icon";
import { Keycap } from "./Keycap";
import { isInAppNavigation, Link } from "./Link";
import { Modal } from "./Modal";
import {
  chatLabel,
  pickChatIconColor,
} from "./PersonalChats/pickChatIconColor";
import { SearchContainer, SearchInput } from "./SearchField";
import { SmallWorkflowBadge } from "./SmallWorkflowBadge";
import { SpaceBadge } from "./SpaceBadge";
import { Text } from "./Text";

const Container = styled("div")`
  width: min(424px, calc(100vw / var(--ui-zoom, 1) - 32px));
  max-width: 100%;
  height: min(440px, 70dvh);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Results = styled("div")`
  flex: 1;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0 4px;
`;

const SectionHeader = styled("div")`
  display: flex;
  padding: 16px 8px 8px;
  color: var(--text-secondary);
`;

const rowStyle = `
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  color: var(--text-primary);
  text-decoration: none;
  &[aria-selected="true"] {
    background: var(--alpha4);
  }
  & > .select-hint {
    visibility: hidden;
    flex-shrink: 0;
    margin-left: auto;
  }
  &[aria-selected="true"] > .select-hint {
    visibility: visible;
  }
`;

const LinkRow = styled(Link)`
  ${rowStyle}
`;

const ButtonRow = styled("button")`
  ${rowStyle}
  border: none;
  background: none;
  font: inherit;
  text-align: left;
`;

const Footer = styled("div")`
  display: flex;
  height: 40px;
  flex-shrink: 0;
  padding: 0 12px;
  box-shadow: inset 0 0.5px 0 var(--border-alpha);
  justify-content: space-between;
  align-items: center;
`;

const FooterLogo = styled("div")`
  display: flex;
  opacity: 0.2;
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 6px;
`;

type CommandItem = {
  id: string;
  label: string;
  icon: ReactNode;
  detail?: ReactNode;
  keywords?: string;
} & ({ to: string; onSelect?: never } | { onSelect: () => void; to?: never });

type CommandSection = { id: string; label: string; items: CommandItem[] };

function CommandResult({
  item,
  selected,
  id,
  rowRef,
  onHighlight,
  onClose,
}: {
  item: CommandItem;
  selected: boolean;
  id: string;
  rowRef: (element: HTMLElement | null) => void;
  onHighlight: () => void;
  onClose: () => void;
}) {
  const props = {
    id,
    ref: rowRef,
    role: "option",
    "aria-selected": selected,
    tabIndex: -1,
    onMouseEnter: onHighlight,
  };
  const content = (
    <>
      <span aria-hidden="true" style={{ display: "flex", flexShrink: 0 }}>
        {item.icon}
      </span>
      <span style={{ minWidth: 0 }}>
        <Text ellipsis variant="text-regular">
          {item.label}
        </Text>
      </span>
      {item.detail && (
        <span style={{ flexShrink: 0, maxWidth: "40%", minWidth: 0 }}>
          {item.detail}
        </span>
      )}
      <Keycap className="select-hint" aria-hidden="true">
        <Icon name="corner-down-left" size={12} />
      </Keycap>
    </>
  );
  if (item.onSelect) {
    return (
      <ButtonRow {...props} type="button" data-no-pill onClick={item.onSelect}>
        {content}
      </ButtonRow>
    );
  }
  return (
    <LinkRow
      {...props}
      to={item.to}
      onClick={(event) => {
        if (isInAppNavigation(event)) onClose();
      }}
    >
      {content}
    </LinkRow>
  );
}

export function CommandBar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const rowRefs = useRef(new Map<string, HTMLElement>());
  const scrollToIdRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsId = useId();
  const makeUrl = useMakeUrl();
  const z = useZero();
  const createConnector = useCreateConnector();
  const personalWorkEnabled = usePersonalWorkEnabled();
  const hasQuery = query.trim().length > 0;
  const [allWorkflows] = useQuery(queries.allWorkflows());
  const [workflowViews] = useQuery(queries.userWorkflowViews());
  const [spaces] = useSpaces({ settingsView: true });
  const [chats] = useQuery(
    personalWorkEnabled
      ? queries.personalChatTabs({ includeClosed: true })
      : null
  );
  const aiSearch = useCommandBarSearch();
  const openedSearch = aiSearch.search;
  const [connectors] = useQuery(hasQuery ? queries.connectorCatalog() : null);
  const [skills] = useQuery(hasQuery ? queries.skillsManagementIndex() : null);
  const displayName = useSpaceDisplayName();
  const spaceById = new Map(spaces.map((space) => [space.id, space]));
  const seenAt = new Map(
    workflowViews.map((view) => [view.workflowId, view.lastSeenAt])
  );
  const matching = (items: CommandItem[]) => {
    const terms = query.trim().toLowerCase().split(/\s+/);
    return items.filter((item) =>
      terms.every((term) =>
        `${item.label} ${item.keywords ?? ""}`.toLowerCase().includes(term)
      )
    );
  };
  const workflowItems: CommandItem[] = allWorkflows
    .filter((item) => item.deletedAt == null)
    .toSorted(
      (a, b) =>
        (seenAt.get(b.id) ?? 0) - (seenAt.get(a.id) ?? 0) ||
        a.name.localeCompare(b.name)
    )
    .filter((item) => hasQuery || seenAt.has(item.id))
    .map((item) => {
      const space = spaceById.get(item.spaceId);
      return {
        id: `workflow:${item.id}`,
        label: item.name,
        keywords: space ? displayName(space) : undefined,
        icon: <SmallWorkflowBadge workflow={item} />,
        detail: space ? (
          <SpaceBadge space={space} interactive={false} />
        ) : undefined,
        to: makeUrl(
          routes.space.workflow,
          {
            spaceId: item.spaceId,
            workflowId: item.id,
          },
          { inheritParams: false }
        ),
      };
    });
  const chatItems: CommandItem[] = (
    personalWorkEnabled ? [...(chats ?? [])] : []
  )
    .filter((chat) => chat.deletedAt == null)
    .sort((a, b) => b.lastActiveAt - a.lastActiveAt)
    .map((chat) => ({
      id: `chat:${chat.id}`,
      label: chatLabel(chat.name),
      icon: (
        <Icon
          name="ask"
          size={16}
          color={`var(--${pickChatIconColor(chat.id)}-600)`}
        />
      ),
      to: makeUrl(
        routes.chats,
        {
          tabId: chat.id,
        },
        { inheritParams: false }
      ),
    }));
  const catalog: CommandSection[] = [
    {
      id: "chats",
      label: hasQuery ? "Chats" : "Recent chats",
      items: hasQuery ? chatItems : chatItems.slice(0, 5),
    },
    {
      id: "workflows",
      label: hasQuery ? "Workflows" : "Recent workflows",
      items: hasQuery ? workflowItems : workflowItems.slice(0, 5),
    },
    {
      id: "connectors",
      label: "Connectors",
      items: (hasQuery ? (connectors ?? []) : []).map((connector) => ({
        id: `connector:${connector.id}`,
        label: connector.name,
        keywords: connectorsByIdentifier[connector.identifier]?.keywords,
        icon: <ConnectorLogo app={connector} size={16} />,
        to: makeUrl(routes.modal.connector, { id: connector.id }),
      })),
    },
    {
      id: "skills",
      label: "Skills",
      items: (hasQuery ? (skills ?? []) : [])
        .filter((skill) => !skill.fresh || skill.gitInstallation !== undefined)
        .map((skill) => ({
          id: `skill:${skill.id}`,
          label: skill.name,
          keywords: skill.description,
          icon: <Icon name="skills" size={16} />,
          to: makeUrl(routes.modal.skill, { id: skill.id }),
        })),
    },
    {
      id: "spaces",
      label: "Spaces",
      items: (hasQuery ? spaces : [])
        .filter((space) => personalWorkEnabled || space.mode !== "personal")
        .map((space) => ({
          id: `space:${space.id}`,
          label: displayName(space),
          icon: <Icon name={spaceIcon(space.mode)} size={16} />,
          to: makeUrl(
            routes.space,
            { spaceId: space.id },
            { inheritParams: false }
          ),
        })),
    },
  ];
  const addedIdentifiers = new Set(
    (connectors ?? []).map((connector) => connector.identifier)
  );
  const connectorTypeItem = (typeIdentifier: string): CommandItem[] => {
    const config = connectorDirectory.find(
      (app) => connectorTypeOf(app.identifier)?.identifier === typeIdentifier
    );
    if (!config || !createConnector.canCreate) return [];
    return [
      {
        id: `connectorType:${typeIdentifier}`,
        label: config.name,
        icon: <ConnectorLogo template={config} size={16} />,
        detail: <NotAddedPill />,
        onSelect: () => {
          const id = createConnector.create(config);
          onClose();
          navigate(routes.modal.connector, { id });
        },
      },
    ];
  };
  const itemsById = new Map(
    catalog.flatMap((section) => section.items).map((item) => [item.id, item])
  );
  const sections: CommandSection[] = openedSearch
    ? [
        {
          id: "found",
          label: "Found by 3B",
          items: openedSearch.results.flatMap((id) => {
            const item = itemsById.get(id);
            if (item) return item;
            return id.startsWith("connectorType:")
              ? connectorTypeItem(id.slice("connectorType:".length))
              : [];
          }),
        },
      ]
    : [
        ...catalog.flatMap((section) => [
          { ...section, items: matching(section.items) },
          ...(section.id === "connectors" && hasQuery
            ? [
                {
                  id: "connectorTypes",
                  label: "Add a connector",
                  items: findConnectorTypes({ search: query })
                    .filter(
                      (type) =>
                        !type.variants.some((variant) =>
                          addedIdentifiers.has(variant.identifier)
                        )
                    )
                    .flatMap((type) => connectorTypeItem(type.identifier))
                    .slice(0, 5),
                },
              ]
            : []),
        ]),
      ];
  const allItems = sections.flatMap((section) => section.items);
  const selected =
    allItems.find((item) => item.id === selectedId) ?? allItems[0];
  const startSearch = () => {
    aiSearch.start(query.trim());
    setSelectedId(null);
  };
  const askAgent = () => {
    const chat = { id: nanoid(12), chatId: nanoid(12) };
    z.mutate(sharedMutators.createPersonalChat(chat));
    z.mutate(
      sharedMutators.sendPersonalChatMessage({
        chatId: chat.chatId,
        entryId: nanoid(12),
        text: `Help me find this:\n> ${query.trim()}`,
        actionKind: "search",
        ...clientSendContext(),
      })
    );
    handOffPendingPersonalChat(chat);
    onClose();
    navigate(routes.chats, { tabId: chat.id });
  };
  const changeQuery = (value: string) => {
    setQuery(value);
    setSelectedId(null);
    aiSearch.stop();
  };
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery("");
      setSelectedId(null);
    }
  }
  useEffect(() => {
    if (selected?.id === scrollToIdRef.current) {
      rowRefs.current.get(selected.id)?.scrollIntoView({ block: "nearest" });
    }
    scrollToIdRef.current = null;
  }, [selected?.id]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      initialFocusRef={inputRef}
      style={{ marginTop: "20vh", marginBottom: "auto", overflowX: "hidden" }}
    >
      <Container>
        <SearchContainer style={{ flexShrink: 0, paddingTop: 1 }}>
          <Icon name="search" size={16} />
          <SearchInput
            style={{ minWidth: 0 }}
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label="Search"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={resultsId}
            aria-activedescendant={
              selected ? `${resultsId}-option-${selected.id}` : undefined
            }
            placeholder={
              personalWorkEnabled
                ? "Search chats, workflows, connectors, skills…"
                : "Search workflows, connectors, skills…"
            }
            value={query}
            onChange={(event) => changeQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing) return;
              if (!selected) {
                if (event.key === "Enter" && hasQuery && personalWorkEnabled) {
                  event.preventDefault();
                  event.stopPropagation();
                  if (openedSearch?.status === "searching") aiSearch.stop();
                  else if (openedSearch) askAgent();
                  else startSearch();
                }
                return;
              }
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                event.stopPropagation();
                const index = allItems.indexOf(selected);
                const next =
                  allItems[
                    Math.max(
                      0,
                      Math.min(
                        allItems.length - 1,
                        index + (event.key === "ArrowDown" ? 1 : -1)
                      )
                    )
                  ];
                if (next) {
                  scrollToIdRef.current = next.id;
                  setSelectedId(next.id);
                }
              } else if (event.key === "Enter") {
                event.preventDefault();
                event.stopPropagation();
                rowRefs.current.get(selected.id)?.click();
              }
            }}
          />
        </SearchContainer>
        <Results id={resultsId} role="listbox" aria-label="Search results">
          {sections
            .filter((section) => section.items.length > 0)
            .map((section) => (
              <div
                key={section.id}
                role="group"
                aria-labelledby={`${resultsId}-${section.id}`}
              >
                <SectionHeader id={`${resultsId}-${section.id}`}>
                  <Text variant="text-small">{section.label}</Text>
                </SectionHeader>
                {section.items.map((item) => (
                  <CommandResult
                    key={item.id}
                    item={item}
                    id={`${resultsId}-option-${item.id}`}
                    selected={item.id === selected?.id}
                    rowRef={(element) => {
                      if (element) rowRefs.current.set(item.id, element);
                      else rowRefs.current.delete(item.id);
                    }}
                    onHighlight={() => setSelectedId(item.id)}
                    onClose={onClose}
                  />
                ))}
              </div>
            ))}
          {allItems.length === 0 &&
            (openedSearch ? (
              <SearchStatus
                search={openedSearch}
                onStop={aiSearch.stop}
                onAskAgent={askAgent}
              />
            ) : hasQuery && personalWorkEnabled ? (
              <NoResults onSearch={startSearch} />
            ) : (
              <EmptyState
                iconName="search"
                iconSize={24}
                maxWidth={220}
                title={hasQuery ? "No results" : "Search your workspace"}
                description={
                  hasQuery
                    ? "Try a different search term."
                    : personalWorkEnabled
                      ? "Find chats, workflows, connectors, skills, and spaces."
                      : "Find workflows, connectors, skills, and spaces."
                }
              />
            ))}
        </Results>
        <Footer>
          <FooterLogo>
            <Icon name="tines-mark" size={16} />
          </FooterLogo>
          <Instructions>
            <Text variant="text-smaller-md">Navigate</Text>
            <div style={{ display: "flex", gap: 2 }}>
              <Keycap>
                <Icon name="arrow-up" size={12} />
              </Keycap>
              <Keycap>
                <Icon name="arrow-down" size={12} />
              </Keycap>
            </div>
            <Icon name="slash" size={12} color="var(--border-alpha)" />
            <Text variant="text-smaller-md">Select</Text>
            <Keycap>
              <Icon name="corner-down-left" size={12} />
            </Keycap>
            <Icon name="slash" size={12} color="var(--border-alpha)" />
            <Text variant="text-smaller-md">Close</Text>
            <Keycap style={{ padding: "2px 4px" }}>
              <Text variant="text-smaller-md">esc</Text>
            </Keycap>
          </Instructions>
        </Footer>
      </Container>
    </Modal>
  );
}
